import { useEffect, useState } from "react";
import { Loader2, Feather, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SaidSections, SuggestionBox, MemberReflectionForm, AlignmentCompass } from "./ReflectionView";

const recOptions = (recs = {}) => [
  ...(recs.services || []).map((s) => ({ title: s.title, type: "individual session" })),
  ...(recs.events || []).map((e) => ({ title: e.title, type: e.is_virtual ? "virtual event" : "event" })),
  ...(recs.community || []).map((g) => ({ title: g.name, type: "community group" })),
  ...(recs.courses || []).map((c) => ({ title: c.title, type: c.access === "free" ? "free course" : "course" })),
  ...(recs.practices || []).map((p) => ({ title: p.title, type: "free independent practice" })),
  ...(recs.matched_enhancer ? [{ title: recs.matched_enhancer.name, type: "enhancer" }] : []),
];

function Answer({ q, value, onChange }) {
  const opts = q.options || [];
  if (q.question_type === "multi_select" || q.question_type === "single_choice") {
    const arr = Array.isArray(value) ? value : value ? [value] : [];
    return (
      <div className="flex flex-wrap gap-2" role="group" aria-label={q.question_text}>
        {opts.map((o) => {
          const on = arr.includes(o.key);
          const next = q.question_type === "single_choice" ? (on ? "" : o.key) : (on ? arr.filter((k) => k !== o.key) : [...arr, o.key]);
          return <button type="button" key={o.key} aria-pressed={on} onClick={() => onChange(next)} data-testid={`deeper-opt-${q.id}-${o.key}`}
            className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${on ? "border-[#144F75] bg-[#144F75]/[0.08] text-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40"}`}>{o.label}</button>;
        })}
      </div>
    );
  }
  return (
    <div>
      <textarea rows={3} value={value === "not_sure" ? "" : value || ""} onChange={(e) => onChange(e.target.value)} aria-label={q.question_text}
        placeholder={q.placeholder_text || "A few words is plenty…"} data-testid={`deeper-text-${q.id}`} className="eyw-input w-full resize-none" />
      <button type="button" onClick={() => onChange(value === "not_sure" ? "" : "not_sure")} aria-pressed={value === "not_sure"} data-testid={`deeper-notsure-${q.id}`}
        className={`mt-1 text-xs rounded-full px-2.5 py-1 border ${value === "not_sure" ? "border-[#144F75] text-[#144F75]" : "border-transparent text-[#8FA89B] hover:text-[#144F75]"}`}>I'm not sure</button>
    </div>
  );
}

// Optional deeper exploration → personal reflection (own words + labelled AI suggestion).
export default function GaugeDeeper({ result, path, responses, saved }) {
  const [data, setData] = useState(null);
  const [lenses, setLenses] = useState([]);
  const [answers, setAnswers] = useState({});
  const [reflection, setReflection] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (ls) => {
    try {
      const { data: d } = await api.post("/gauge/deeper", {
        path_slug: path.slug, primary_orientation: result.primary_orientation, orientation_scores: result.orientation_scores || {},
        answered: Object.keys(responses), lenses: ls,
      });
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLens = (k) => { const ls = lenses.includes(k) ? lenses.filter((x) => x !== k) : [...lenses, k]; setLenses(ls); load(ls); };

  const create = async () => {
    setBusy(true);
    const base = { path_slug: path.slug, exploration_id: result.exploration_id || "", responses, deeper: answers, options: recOptions(result.recommendations) };
    try {
      const { data: quick } = await api.post("/gauge/reflection", { ...base, include_suggestion: false });
      setReflection(quick); setBusy(false); setSuggesting(true);
      const { data: full } = await api.post("/gauge/reflection", base);
      setReflection(full);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setSuggesting(false); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#144F75]" /></div>;
  const answeredCount = Object.values(answers).filter((v) => (Array.isArray(v) ? v.length : v)).length;

  return (
    <section className="eyw-card p-6 lg:p-8 mt-8" data-testid="gauge-deeper">
      <p className="eyw-label !text-[#D98E73] flex items-center gap-1.5"><Feather size={13} /> Go a little deeper · optional</p>
      <h2 className="font-serif text-2xl text-[#1A242B] mt-1">What feels most important today?</h2>
      <p className="text-sm text-[#4A5D6B] mt-1">Answer any, skip any. Your words shape a personal reflection{saved ? " that's saved privately to your journey" : " (sign in to save it)"}.</p>
      <div className="flex flex-wrap gap-2 mt-4" data-testid="deeper-lenses">
        {data.lenses.map((l) => (
          <button key={l.key} onClick={() => toggleLens(l.key)} aria-pressed={lenses.includes(l.key)} data-testid={`deeper-lens-${l.key}`} title={l.blurb}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${lenses.includes(l.key) ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40"}`}>{l.label}</button>
        ))}
      </div>
      {!lenses.length && <p className="text-xs text-[#8FA89B] mt-2">We've picked a few questions from what you shared. Choose an area above to see more.</p>}

      {!reflection && (
        <div className="mt-6 space-y-6">
          {data.questions.map((q) => (
            <div key={q.id} data-testid={`deeper-q-${q.id}`}>
              <p className="font-medium text-[#1A242B]">{q.question_text}</p>
              <p className="text-xs text-[#8FA89B] mb-2">{q.helper_text || "Optional"}</p>
              <Answer q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
            </div>
          ))}
          <button onClick={create} disabled={busy} data-testid="deeper-create-reflection" className="eyw-btn disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} {answeredCount ? "Create my reflection" : "Create my reflection from what I've shared"} <ArrowRight size={15} />
          </button>
        </div>
      )}

      {reflection && (
        <div className="mt-6 space-y-5" data-testid="gauge-reflection">
          <div className="rounded-2xl bg-[#F9F7F3] p-5">
            <p className="font-serif text-xl text-[#1A242B] mb-3">In your words</p>
            <SaidSections said={reflection.said} />
          </div>
          <AlignmentCompass said={reflection.said} />
          <SuggestionBox suggestion={reflection.suggestion} loading={suggesting} />
          {!suggesting && !reflection.suggestion && <p className="text-xs text-[#8FA89B]" data-testid="reflection-no-suggestion">No AI suggestion this time — your own words above are the reflection.</p>}
          {reflection.saved && result.exploration_id && <MemberReflectionForm explorationId={result.exploration_id} suggestions={reflection.suggestion?.next_steps || []} />}
          <button onClick={() => setReflection(null)} data-testid="deeper-edit-answers" className="text-sm text-[#144F75] underline">Change my answers</button>
        </div>
      )}
    </section>
  );
}
